/**
 * Shared types between the side panel and the zoomnotes-worker.
 * Mirror of the Worker's `routes.ts` + `anthropic.ts` shapes.
 */

export type TemplateName =
  // Stage-based sales (general)
  | 'sales-discovery'
  | 'sales-followup'
  // Vertical-specific sales
  | 'sales-fintech'
  | 'sales-banking'
  | 'sales-private-market'
  | 'sales-ai-solutions'
  // Internal — marketing
  | 'marketing-customer-interview'
  | 'marketing-campaign-planning'
  // Internal — finance
  | 'finance-budget-review'
  // Catch-all
  | 'generic'

export const TEMPLATE_LABELS: Record<TemplateName, string> = {
  'sales-discovery': 'Sales — Discovery',
  'sales-followup': 'Sales — Follow-up',
  'sales-fintech': 'Sales — Fintech',
  'sales-banking': 'Sales — Banking',
  'sales-private-market': 'Sales — PE / VC / Investors',
  'sales-ai-solutions': 'Sales — AI Solutions',
  'marketing-customer-interview': 'Marketing — Customer interview',
  'marketing-campaign-planning': 'Marketing — Campaign planning',
  'finance-budget-review': 'Finance — Budget review',
  generic: 'Generic',
}

export const TEMPLATE_HINTS: Record<TemplateName, string> = {
  'sales-discovery':
    'First call with a prospect. Surfaces BANT/MEDDIC qualification gaps.',
  'sales-followup':
    'Post-call wrap-up. Concrete next-step actions, no email draft.',
  'sales-fintech':
    'Selling AI to fintech buyers. Regulatory, sponsor-bank, time-to-market focus.',
  'sales-banking':
    'Selling AI to banks. Long cycles, peer case studies, core-system integration.',
  'sales-private-market':
    'Selling AI to PE / VC / family offices. Deal flow + portfolio value creation.',
  'sales-ai-solutions':
    'Cross-vertical AI sales. Hallucination, ROI, build-vs-buy concerns.',
  'marketing-customer-interview':
    'Customer-research interview. Extracts verbatim quotes + persona signals.',
  'marketing-campaign-planning':
    'Internal campaign planning. Audience, channels, owners, dates.',
  'finance-budget-review':
    'Variance, runway, headcount, vendor-spend review.',
  generic: 'Catch-all template when no other one fits.',
}

export interface MeetingContext {
  title?: string
  participants?: string[]
  startTime?: string
}

export interface Idea {
  text: string
}

/**
 * Simplified schema (collapsed from 6 sections into 1 list of ideas + a
 * one-line summary). Keep in sync with the Worker's `anthropic.ts`.
 */
export interface SuggestionsPayload {
  summary: string
  ideas: Idea[]
}

export interface SuggestResponseMeta {
  model: string
  latencyMs: number
  usage: { inputTokens: number; outputTokens: number }
}

export interface SuggestResponse extends SuggestionsPayload {
  template: TemplateName
  _meta: SuggestResponseMeta
}

/* ────────────────────────────────────────────────────────────────────────
 * Chat-mode types — for the conversation-style side panel.
 *
 * Discriminated union: each message is either user-typed or AI-generated.
 * The `kind` field is the discriminator; TypeScript narrows safely on it.
 * ──────────────────────────────────────────────────────────────────────── */

export interface UserChatMessage {
  kind: 'user'
  id: string
  ts: string
  content: string
}

export interface AssistantChatMessage {
  kind: 'assistant'
  id: string
  ts: string
  /** Filled in when the API call resolves; null while pending. */
  suggestions: SuggestionsPayload | null
  meta: SuggestResponseMeta | null
  isLoading: boolean
  error: string | null
  /** Template active when this message was generated. */
  template: TemplateName
}

export type ChatMessage = UserChatMessage | AssistantChatMessage
